import { SESSION_STATUS_DISPLAY_ORDER } from '@/components/ui/session-status/session-status-order';
import type { ChipColor } from '@heidi/design-system/chip';
import type { EnumCellOption } from '@heidi/design-system/data-table';
import {
  StatusIndicator,
  type StatusIndicatorStatus,
} from '@heidi/design-system/status-indicator';
import { SessionStatus } from '@heidi/schema/ml-scribe';
import type { IntlShape } from 'react-intl';

interface SessionStatusChipConfig {
  color: ChipColor;
  indicator: StatusIndicatorStatus;
}

// Mirrors the StatusIndicator palette onto the chip so the chip surface and
// the leading indicator glyph share a single semantic colour identity.
export const sessionStatusChipConfig: Record<SessionStatus, SessionStatusChipConfig> = {
  [SessionStatus.EMPTY]: { color: 'neutral', indicator: 'empty' },
  [SessionStatus.DRAFT]: { color: 'blue', indicator: 'in-progress' },
  [SessionStatus.FOR_ACTION]: { color: 'purple', indicator: 'to-do' },
  [SessionStatus.REVIEWED]: { color: 'orange', indicator: 'in-review' },
  [SessionStatus.APPROVED]: { color: 'green', indicator: 'ready' },
  [SessionStatus.SENT]: { color: 'yellow', indicator: 'done' },
};

// Inline enum-cell trigger and `SessionStatus` header dropdown share this
// option set so they resolve to the same colour + indicator for a given status.
export const buildSessionStatusOptions = (
  intl: IntlShape
): EnumCellOption<SessionStatus>[] => {
  const labels: Record<SessionStatus, string> = {
    [SessionStatus.EMPTY]: intl.formatMessage({
      defaultMessage: 'No status',
      id: 'aFope9eAke',
    }),
    [SessionStatus.DRAFT]: intl.formatMessage({
      defaultMessage: 'Draft',
      id: 'W6nwjoOm/V',
    }),
    [SessionStatus.FOR_ACTION]: intl.formatMessage({
      defaultMessage: 'For action',
      id: 'SiSdW9bmda',
    }),
    [SessionStatus.REVIEWED]: intl.formatMessage({
      defaultMessage: 'Reviewed',
      id: 'wiy+GNv+BV',
    }),
    [SessionStatus.APPROVED]: intl.formatMessage({
      defaultMessage: 'Approved',
      id: '6XFO/Cw7Ld',
    }),
    [SessionStatus.SENT]: intl.formatMessage({
      defaultMessage: 'Sent',
      id: 'gcuDcHiCz7',
    }),
  };
  return SESSION_STATUS_DISPLAY_ORDER.map((value) => {
    const config = sessionStatusChipConfig[value];
    return {
      value,
      label: labels[value],
      leadingIcon: <StatusIndicator status={config.indicator} size="xs" />,
      color: config.color,
    };
  });
};
